<!--
 * @Author: Tahiti
 * @Date: 2025-10-20 13:34:19
 * @LastEditors: 冯伟绩 fengweiji@wekoi.cn
 * @LastEditTime: 2026-01-13 17:40:00
 * @Description: 
 * @FilePath: /weryai-web/layouts/default.vue
-->
<template>
    <div>
        <!-- 顶部导航栏：移动端统一使用 MobileHomeHeader -->
        <!-- 使用 ClientOnly 避免 SSR 水合不匹配（isSmallScreen 在服务端为 false，客户端可能为 true） -->
        <ClientOnly>
            <MobileHomeHeader v-if="!mobileHiddenHeader" />
        </ClientOnly>
        <main class="flex-1 bg-default min-h-screen">
            <app-sidebar></app-sidebar>
            <div class="main-content overflow-x-hidden mx-auto w-full max-md:pl-0 max-lg:ml-0 lg:flex-1 max-md:!px-3 md:pr-5 transition-[padding-left] duration-300 ease-in-out max-md:pb-24 pl-[var(--sidebar-width)]"
                :style="{
                    '--sidebar-width': `${appStore.sidebarWidth + 15}px`,
                }" :class="{ 'is-api-route': isApiRoute, 'is-explore-route': isExploreRoute }">
                <slot />
            </div>
        </main>

        <!-- PWA 添加到桌面提示卡片（全局） -->
        <ClientOnly>
            <ToolPwaPromptCard />
        </ClientOnly>
        <!-- 移动端底部菜单：使用 ClientOnly 避免 SSR 水合不匹配 -->
        <ClientOnly>
            <mobile-footer-menu class="md:hidden block"></mobile-footer-menu>
        </ClientOnly>

        <!-- 移动端创作弹窗 - 使用 ClientOnly 避免 Teleport SSR 问题 -->
        <ClientOnly>
            <MobileCreationModal />
        </ClientOnly>

        <!-- 签到组件 - 移动端需要单独渲染（AppSidebar 在移动端 CSS 隐藏，需要独立的事件监听器） -->
        <ClientOnly>
            <SignInButton v-if="SIGN_IN_ENABLED && userInfo && appStore.isSmallScreen" />
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import { supportedLanguages } from '~/locales'
import { usePwaPrompt } from '~/composables/usePwaPrompt'
import { SIGN_IN_ENABLED } from '~/utils/feature-flags'

const appStore = useAppStore()
const userStore = useUserStore()

// 用户信息
const userInfo = computed(() => userStore.userInfo)
const { checkAndReportPwaTask, initBeforeInstallPrompt } = usePwaPrompt()

// 初始化 PWA 相关功能
onMounted(() => {
    // 初始化 beforeinstallprompt 事件监听（用于 Android 一键安装）
    initBeforeInstallPrompt()
    // 检测 PWA 模式并上报任务完成
    checkAndReportPwaTask()
})
const route = useRoute()

// 从公共配置中获取支持的语言列表
const SUPPORTED_LOCALES = supportedLanguages

// 去掉路径中的语言前缀
const stripLocalePrefix = (path: string) => {
    const match = path.match(/^\/([a-z]{2})(\/|$)/)
    const lang = match?.[1]
    if (lang && SUPPORTED_LOCALES.includes(lang as any)) {
        return path.replace(new RegExp(`^/${lang}`), '') || '/'
    }
    return path
}

// 判断是否是 API 路由（需要去除语言前缀后再判断）
const isApiRoute = computed(() => {
    const pathWithoutLocale = stripLocalePrefix(route.path)
    return pathWithoutLocale.startsWith('/api')
})

// 判断是否是 explore 路由（需要去除语言前缀后再判断）
const isExploreRoute = computed(() => {
    const pathWithoutLocale = stripLocalePrefix(route.path)
    return pathWithoutLocale.startsWith('/explore')
})



const getCurrentPathname = () => {
    // Important: ensure this computed depends on the reactive route,
    // otherwise SPA navigation won't trigger recomputation.
    const _routePath = route.path
    return import.meta.client && typeof window !== 'undefined'
        ? window.location.pathname
        : _routePath || '/'
}

// my-creations不展示顶部导航栏
const mobileHiddenHeader = computed(() => {
    const p = getCurrentPathname()
    const isMyCreations = /^\/(?:[a-z]{2}\/)?my-creations(?:\/|$)/.test(p)
    return appStore.isSmallScreen && isMyCreations
})
</script>

<style scoped lang="less">
/* API 路由样式层叠 - 使用 !important 确保最高优先级 */
.main-content.is-api-route {
    padding-top: 0 !important;

    /* 移动端样式 */
    @media (max-width: 768px) {
        padding-top: 0 !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
    }

    /* 桌面端样式 */
    @media (min-width: 768px) {
        padding-right: 0 !important;
    }
}

/* Explore 路由样式 - 去掉顶部内边距 */
.main-content.is-explore-route {
    padding-top: 0 !important;
    overflow-x: visible !important; // 修复 sticky 定位失效问题
}
</style>
